import React, { createContext, useContext, useState, useEffect } from "react";
import { Pharmacy } from "@/types";
import api from "@/services/api";
import { useAuth } from "./AuthContext";

interface PharmacyContextType {
  currentPharmacy?: Pharmacy;
  availablePharmacies: Pharmacy[];
  loading: boolean;
  refreshPharmacies: () => void;
}

const PharmacyContext = createContext<PharmacyContextType | undefined>(
  undefined
);

export function PharmacyProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [currentPharmacy, setCurrentPharmacy] = useState<
    Pharmacy | undefined
  >();
  const [availablePharmacies, setAvailablePharmacies] = useState<Pharmacy[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchPharmacies = async () => {
    // Prevent duplicate calls
    if (loading) return;

    // Don't fetch if auth is still loading or user is not authenticated
    if (authLoading || !isAuthenticated) {
      return;
    }

    try {
      setLoading(true);

      const currentPharmacyId = localStorage.getItem("currentPharmacyId");
      const params: Record<string, string> = {};
      if (currentPharmacyId) {
        params.pharmacy_id = currentPharmacyId;
      }

      const response = await api.get("/pharmacy/dashboard", { params });

      if (response.data.currentPharmacy) {
        setCurrentPharmacy(response.data.currentPharmacy);
        localStorage.setItem(
          "currentPharmacyId",
          response.data.currentPharmacy.id
        );
      }

      if (response.data.availablePharmacies) {
        setAvailablePharmacies(response.data.availablePharmacies);
      }

      setHasFetched(true);
    } catch (error) {
      console.error("Failed to fetch pharmacy data:", error);
      // Don't show error for unauthenticated users
      const err = error as any;
      if (err.response?.status !== 401) {
        console.error("Pharmacy fetch error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset pharmacy data when user logs out or changes
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentPharmacy(undefined);
      setAvailablePharmacies([]);
      setHasFetched(false);
    }
  }, [isAuthenticated]);

  // Fetch pharmacies when user changes (new login)
  useEffect(() => {
    if (!authLoading && isAuthenticated && !hasFetched) {
      fetchPharmacies();
    }
  }, [authLoading, isAuthenticated, hasFetched, user?.email]);

  const refreshPharmacies = () => {
    setHasFetched(false);
  };

  return (
    <PharmacyContext.Provider
      value={{
        currentPharmacy,
        availablePharmacies,
        loading,
        refreshPharmacies,
      }}
    >
      {children}
    </PharmacyContext.Provider>
  );
}

export function usePharmacyContext() {
  const context = useContext(PharmacyContext);
  if (context === undefined) {
    throw new Error(
      "usePharmacyContext must be used within a PharmacyProvider"
    );
  }
  return context;
}
